import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useCoarsePointer } from '@/hooks/use-coarse-pointer';
import { cn } from '@/lib/utils';
import { type DraftFieldErrorCode } from './api';
import { type AdditionalDataDraft } from './content';
import { fieldErrorKey } from './fieldErrors';

/**
 * Suggested `DodatkowyOpis` keys offered by the key combobox; the field stays free-text, so any custom
 * key can be typed. These are the literal `Klucz` strings written to the invoice (Polish, as the FA(3)
 * document is), mirroring how the unit picker offers a fixed set of literal units.
 */
const KEY_OPTIONS = [
  'Numer rejestracyjny pojazdu',
  'Numer zamówienia',
  'Dokument powiązany',
  'Uwagi',
] as const;

/**
 * Editor for the invoice's additional-data (`DodatkowyOpis`) key/value rows: a repeatable list where
 * each row pairs a free-text-but-suggested key with a value. Rows carry no order semantics, so — unlike
 * the line editor — they are not draggable.
 */
export function AdditionalDataEditor({
  rows,
  rowErrors,
  disabled,
  onPatch,
  onAdd,
  onRemove,
}: {
  rows: AdditionalDataDraft[];
  rowErrors: Map<number, DraftFieldErrorCode[]>;
  disabled?: boolean;
  onPatch: (id: number, patch: Partial<AdditionalDataDraft>) => void;
  onAdd: () => void;
  onRemove: (id: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('invoices.compose.additional_data.empty')}
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <AdditionalDataRow
              key={row.id}
              row={row}
              codes={rowErrors.get(row.id) ?? []}
              disabled={disabled}
              onPatch={onPatch}
              onRemove={onRemove}
            />
          ))}
        </div>
      )}
      <Button type="button" variant="outline" disabled={disabled} onClick={onAdd}>
        <Plus data-icon="inline-start" />
        {t('invoices.compose.additional_data.add')}
      </Button>
    </div>
  );
}

function AdditionalDataRow({
  row,
  codes,
  disabled,
  onPatch,
  onRemove,
}: {
  row: AdditionalDataDraft;
  codes: DraftFieldErrorCode[];
  disabled?: boolean;
  onPatch: (id: number, patch: Partial<AdditionalDataDraft>) => void;
  onRemove: (id: number) => void;
}) {
  const { t } = useTranslation();
  const errorFor = (code: DraftFieldErrorCode) =>
    codes.includes(code) ? t(fieldErrorKey(code)) : undefined;
  // TOO_LONG flags the row (either side); surface it on the value field, the common offender.
  const keyError = errorFor('ADDITIONAL_DATA_KEY_MISSING');
  const valueError = errorFor('ADDITIONAL_DATA_VALUE_MISSING') ?? errorFor('ADDITIONAL_DATA_TOO_LONG');

  return (
    <div className="rounded-md border p-3">
      <div className="flex flex-wrap items-start gap-2">
        <Field className="min-w-40 flex-1" error={keyError}>
          <KeyPicker
            value={row.key}
            disabled={disabled}
            invalid={Boolean(keyError)}
            onChange={(key) => onPatch(row.id, { key })}
          />
        </Field>
        <Field className="min-w-40 flex-1" error={valueError}>
          <Input
            aria-label={t('invoices.compose.additional_data.value')}
            placeholder={t('invoices.compose.additional_data.value')}
            autoComplete="off"
            disabled={disabled}
            aria-invalid={valueError ? true : undefined}
            value={row.value}
            onChange={(e) => onPatch(row.id, { value: e.target.value })}
          />
        </Field>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled}
          aria-label={t('invoices.compose.additional_data.remove')}
          onClick={() => onRemove(row.id)}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Editable key field: a free-text input with a dropdown of suggested keys (always the full list, never
 * filtered by what's typed). Picking one fills the field; typing a custom key is fully supported. Mirrors
 * the unit picker in the line editor.
 */
function KeyPicker({
  value,
  onChange,
  disabled,
  invalid,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <Input
        aria-label={t('invoices.compose.additional_data.key')}
        placeholder={t('invoices.compose.additional_data.key')}
        autoComplete="off"
        className="pr-8"
        disabled={disabled}
        aria-invalid={invalid ? true : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            // Mouse-only affordance: the field is free-text, so keep the chevron out of the Tab order
            // — Tab moves straight from the key input to the next field, and keyboard users just type.
            tabIndex={-1}
            disabled={disabled}
            aria-label={t('invoices.compose.additional_data.key_select')}
            className="absolute inset-y-0 right-0 flex items-center pr-2.5 outline-none focus-visible:text-foreground disabled:opacity-50"
          >
            <ChevronsUpDown className="size-3.5 opacity-50" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {KEY_OPTIONS.map((option) => (
            <DropdownMenuItem key={option} onSelect={() => onChange(option)}>
              <Check className={cn('size-4', value === option ? 'opacity-100' : 'opacity-0')} />
              {option}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * Wraps a field, surfacing its error as a hover hint on fine pointers (mirrors the line editor). The
 * wrapper's element type stays identical whether or not an error is present — only the tooltip content
 * toggles — so an error appearing/clearing mid-edit never remounts the `<input>` (which would drop
 * keyboard focus and strand Tab). See `LineField` in `LinesEditor.tsx`.
 */
function Field({
  error,
  className,
  children,
}: {
  error?: string;
  className?: string;
  children: ReactNode;
}) {
  const coarse = useCoarsePointer();
  if (coarse) return <div className={className}>{children}</div>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={className}>{children}</div>
      </TooltipTrigger>
      {error ? <TooltipContent>{error}</TooltipContent> : null}
    </Tooltip>
  );
}
